import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraDashboardPage, SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { RefreshCw } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { CommandCenterFilterBar } from './CommandCenterFilterBar';
import { DeliveryHealth } from './DeliveryHealth';
import { EMPTY_FILTER, isDefaultFilter, isoDay, parseFilter, summaryBody, writeFilter } from './filters';
import { useAttention, useOnHoldSince, usePortfolioDashboard, usePortfolioSummary } from './hooks';
import { KpiPanel } from './KpiPanel';
import { NeedsAttention } from './NeedsAttention';
import { ProgramsTable } from './ProgramsTable';
import { headline } from './readings';
// SAD-999: the Command Center. One question: "across my programs, what will
// hurt us if nobody acts this week?" Two reads whatever the number of
// programs: the portfolio summary (every deliverable figure, scoped to the
// programs the viewer can see) and the portfolio dashboard (each program's
// projected contract number and funding). The filters are the URL.
export function CommandCenterPage() {
    const companyId = useTenantId();
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const filter = useMemo(()=>parseFilter(params), [
        params
    ]);
    const today = isoDay(new Date());
    const body = useMemo(()=>summaryBody(filter, new Date(`${today}T00:00:00`)), [
        filter,
        today
    ]);
    const summary = usePortfolioSummary(body);
    const attention = useAttention(body);
    // Unfiltered: the program list and money do not change with the filters, so
    // a filter change costs the summary alone.
    const dashboard = usePortfolioDashboard({
        companyId
    });
    const setFilter = (next)=>setParams((prev)=>writeFilter(next, prev));
    const s = summary.data?.body;
    const hiddenCount = summary.data?.hiddenCount ?? 0;
    // The summary and dashboard rows do not carry the hold day; the program list does.
    const onHoldById = useOnHoldSince(companyId).data;
    const dashRows = dashboard.data?.programs;
    const dashById = useMemo(()=>new Map((dashRows ?? []).map((r)=>[
                r.programId,
                r
            ])), [
        dashRows
    ]);
    // The summary names every program it reads (short name, else full name);
    // the dashboard row's short name may be empty or hold the contract number. Remember each name seen, so
    // a program filter (which narrows the summary) keeps the others' names.
    const names = useRef(new Map());
    for (const p of s?.programs ?? [])if (p.name) names.current.set(p.programId, p.name);
    const programOptions = useMemo(()=>(dashRows ?? []).map((r)=>({
                id: r.programId,
                label: names.current.get(r.programId) || r.programName || r.contractNumber || 'Untitled program'
            })).sort((a, b)=>a.label.localeCompare(b.label, undefined, {
                sensitivity: 'base'
            })), // names.current grows with each summary; s re-runs this when it does.
    [
        dashRows,
        s
    ]);
    // With a status filter on, the table lists only the programs holding such
    // dates; otherwise every program in view.
    const rows = useMemo(()=>(s?.programs ?? []).filter((p)=>filter.states.length === 0 || (p.counts?.total ?? 0) > 0).map((p)=>({
                s: p,
                d: dashById.get(p.programId),
                onHoldSince: onHoldById?.get(p.programId)
            })), [
        s,
        dashById,
        onHoldById,
        filter.states.length
    ]);
    const programIds = useMemo(()=>(s?.programs ?? []).map((p)=>p.programId), [
        s
    ]);
    const share = async ()=>{
        const url = `${window.location.origin}${location.pathname}${location.search}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied. Anyone with access sees the same filters.');
        } catch  {
            toast.error('Could not copy the link. Copy it from the address bar instead.');
        }
    };
    const isLoading = summary.isLoading;
    // A filter change keeps the last figures on screen until the new ones land:
    // say so, and dim them, rather than pass stale numbers off as current.
    const updating = summary.isPlaceholderData && summary.isFetching;
    const hasError = summary.isError || dashboard.isError;
    const retry = ()=>{
        if (summary.isError) void summary.refetch();
        if (dashboard.isError) void dashboard.refetch();
    };
    const settled = !summary.isLoading && !dashboard.isLoading;
    // EntityTable owns page-state-content once rows render; before that, this
    // sentinel tells the UX gate whether the page is still loading or settled empty.
    const sentinel = !settled ? Testids.pageState.loading : rows.length === 0 ? Testids.pageState.content : undefined;
    const filtered = !isDefaultFilter(filter);
    const emptyState = filtered ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            onClick: ()=>setFilter(EMPTY_FILTER),
            size: "sm",
            variant: "secondary",
            children: "Clear filters"
        }),
        "data-testid": Testids.commandCenter.emptyNoMatch,
        description: "No program in view has deliverables that match these filters. Clear them to see every program again.",
        framed: true,
        title: "No programs match these filters"
    }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
        "data-testid": Testids.commandCenter.emptyNoPrograms,
        description: hiddenCount > 0 ? `${hiddenCount} ${hiddenCount === 1 ? 'program exists' : 'programs exist'}, but you don’t have access to ${hiddenCount === 1 ? 'it' : 'any of them'} yet. Ask a delivery lead to add you to a program.` : 'There are no delivery programs yet. Programs appear here once a contract is set up for delivery.',
        framed: true,
        title: "No programs to show yet"
    });
    const periodWords = filter.period === 'all' ? 'All dates' : 'Current period';
    const asOf = s?.asOf ? new Date(`${s.asOf}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }) : '';
    return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        "data-testid": Testids.commandCenter.page,
        header: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    description: /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.commandCenter.headline,
                        children: isLoading ? 'Reading your programs…' : updating ? 'Updating…' : headline(s)
                    }),
                    meta: asOf ? /*#__PURE__*/ _jsxs(SpecraText, {
                        "data-testid": Testids.commandCenter.asOf,
                        size: "meta",
                        tone: "muted",
                        children: [
                            periodWords,
                            " · as of ",
                            asOf
                        ]
                    }) : null,
                    title: "Command Center"
                }),
                /*#__PURE__*/ _jsx(CommandCenterFilterBar, {
                    companyId: companyId,
                    filter: filter,
                    onChange: setFilter,
                    onShare: share,
                    owners: s?.ownerOptions ?? [],
                    programs: programOptions,
                    teams: s?.teamOptions ?? []
                }),
                s?.mineFallback && filter.scope === 'mine' ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    "data-testid": Testids.commandCenter.mineFallback,
                    tone: "info",
                    variant: "framed",
                    children: "You don’t lead, own a team on or hold a role on any program yet, so My programs shows the whole portfolio: every program you can see."
                }) : null,
                hasError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        iconLeft: /*#__PURE__*/ _jsx(RefreshCw, {}),
                        onClick: retry,
                        size: "sm",
                        variant: "secondary",
                        children: "Retry"
                    }),
                    "data-testid": Testids.commandCenter.errorBanner,
                    tone: "danger",
                    variant: "framed",
                    children: "Couldn’t load the delivery figures. This is a load error, not an empty portfolio; the numbers below may be missing."
                })
            ]
        }),
        children: [
            sentinel && /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                className: "hidden",
                "data-testid": sentinel
            }),
            /*#__PURE__*/ _jsxs("div", {
                "aria-busy": updating || undefined,
                className: updating ? 'space-y-5 opacity-60 transition-opacity sm:space-y-6' : 'space-y-5 sm:space-y-6',
                children: [
                    /*#__PURE__*/ _jsx(KpiPanel, {
                        filter: filter,
                        kpis: s?.kpis ?? [],
                        loading: isLoading,
                        programIds: programIds
                    }),
                    !isLoading && /*#__PURE__*/ _jsx(DeliveryHealth, {
                        filter: filter,
                        summary: s
                    }),
                    /*#__PURE__*/ _jsx(NeedsAttention, {
                        data: attention.data,
                        filter: filter,
                        loading: attention.isLoading,
                        today: s?.asOf || today
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-baseline justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                        headingAs: "h2",
                                        title: "Programs",
                                        variant: "group"
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: [
                                            rows.length.toLocaleString(),
                                            " ",
                                            rows.length === 1 ? 'program' : 'programs',
                                            hiddenCount > 0 && /*#__PURE__*/ _jsxs("span", {
                                                "data-testid": Testids.commandCenter.hiddenCount,
                                                children: [
                                                    ' ',
                                                    "· ",
                                                    hiddenCount,
                                                    " hidden by access"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ProgramsTable, {
                                emptyState: emptyState,
                                error: summary.isError ? summary.error : undefined,
                                isLoading: isLoading,
                                onRetry: retry,
                                rows: rows,
                                today: s?.asOf || today
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
